import { useEffect, useState } from 'react';

import { MicrophoneLevelVisualization } from './MicrophoneLevelVisualization';

interface MicrophoneLevelProps {
  stream?: MediaStream | null;
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Container component that analyzes a MediaStream and displays audio level visualization.
 * Uses MicrophoneLevelVisualization for presentation.
 */
export const MicrophoneLevel: React.FC<MicrophoneLevelProps> = ({
  stream,
  variant = 'default',
  className,
}) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);

  useEffect(() => {
    if (!stream) {
      setWaveformData([]);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.smoothingTimeConstant = 0.3;
    analyser.fftSize = 1024;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const draw = () => {
      analyser.getByteFrequencyData(dataArray);
      // Convert to array for MicrophoneLevelVisualization
      // Calculate average to match the single-value pattern from offscreen
      const avg =
        dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      setWaveformData([avg]);
      requestAnimationFrame(draw);
    };

    draw();

    return () => {
      audioContext.close();
      source.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, [stream, variant]);

  return (
    <MicrophoneLevelVisualization
      waveformData={waveformData}
      variant={variant}
      className={className}
    />
  );
};
